import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { X, Save, Mail, Lock, ShieldCheck, KeyRound } from 'lucide-react';
import { toast } from '../utils/feedback';

const AccountSettings = ({ onClose }) => {
  const { currentUser, updateUserEmail, updateUserPassword, reauthenticate } = useAuth();
  const [email, setEmail] = useState(currentUser?.email || '');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (!currentPassword) {
      setError('Le mot de passe actuel est requis par sécurité.');
      setLoading(false);
      return;
    }

    try {
      // 1. Re-authenticate user first
      await reauthenticate(currentPassword);
      
      let updated = false;
      
      // 2. Update Email if changed
      if (email !== currentUser.email) {
        await updateUserEmail(email);
        updated = true;
      }
      
      // 3. Update Password if provided
      if (newPassword.length >= 6) {
        await updateUserPassword(newPassword);
        updated = true;
      } else if (newPassword.length > 0) {
        setError('Le nouveau mot de passe doit contenir au moins 6 caractères.');
        setLoading(false);
        return;
      }
      
      if (updated) {
        toast('Compte mis à jour et sécurisé', 'success');
        onClose();
      } else {
        onClose();
      }
    } catch (err) {
      console.error(err);
      if (err.code === 'auth/invalid-credential' || err.code === 'auth/wrong-password') {
        setError('Mot de passe actuel incorrect.');
      } else if (err.code === 'auth/email-already-in-use') {
        setError('Cet email est déjà utilisé.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Email invalide.');
      } else {
        setError('Une erreur est survenue lors de la sécurisation.');
      }
    }
    setLoading(false);
  };

  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.85)',
      zIndex: 10000,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '1rem',
      animation: 'fadeIn 0.3s ease-out'
    }}>
      <div style={{ 
        width: '100%', maxWidth: '450px', 
        background: 'rgba(25, 20, 35, 0.95)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        borderRadius: '24px',
        padding: '2.5rem', 
        position: 'relative',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
      }}>
        <button 
          onClick={onClose}
          style={{ position: 'absolute', top: '1.5rem', right: '1.5rem', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer', transition: 'color 0.2s' }}
          onMouseOver={(e) => e.currentTarget.style.color = 'white'}
          onMouseOut={(e) => e.currentTarget.style.color = 'var(--text-secondary)'}
        >
          <X size={24} />
        </button>

        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <div style={{ 
            width: '60px', height: '60px', 
            background: 'rgba(168, 159, 207, 0.1)', 
            borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
            margin: '0 auto 1rem auto',
            color: 'var(--primary-color)'
          }}>
            <ShieldCheck size={32} />
          </div>
          <h2 style={{ color: 'white', margin: 0, fontSize: '1.8rem' }}>Sécurité du Compte</h2>
          <p style={{ color: 'var(--text-secondary)', margin: '0.5rem 0 0 0', fontSize: '0.9rem' }}>Vérification requise pour toute modification</p>
        </div>

        {error && (
          <div style={{ backgroundColor: 'rgba(255, 107, 107, 0.15)', color: '#ff8787', padding: '1rem', borderRadius: '12px', marginBottom: '1.5rem', textAlign: 'center', fontSize: '0.9rem', border: '1px solid rgba(255, 107, 107, 0.3)' }}>
            {error}
          </div>
        )}

        <form onSubmit={handleUpdate} style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          
          {/* Section Danger/Sécurité - Mot de passe actuel */}
          <div style={{ padding: '1.5rem', background: 'rgba(0,0,0,0.2)', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'white', marginBottom: '0.8rem', fontSize: '0.9rem', fontWeight: '500' }}>
                <KeyRound size={16} color="var(--primary-color)" /> Mot de passe actuel
              </label>
              <input 
                type="password"
                style={{
                  width: '100%', padding: '1rem 1.2rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px', color: 'white',
                  fontSize: '1rem', outline: 'none', transition: 'all 0.3s'
                }}
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Obligatoire pour valider"
                required
              />
            </div>
          </div>

          <div style={{ height: '1px', background: 'rgba(255,255,255,0.1)' }} />

          {/* Section modifications */}
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', marginBottom: '0.8rem', fontSize: '0.9rem' }}>
              <Mail size={16} /> Adresse E-mail
            </label>
            <input 
              type="email"
              style={{
                width: '100%', padding: '1rem 1.2rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px', color: 'white',
                fontSize: '1rem', outline: 'none'
              }}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', marginBottom: '0.8rem', fontSize: '0.9rem' }}>
              <Lock size={16} /> Nouveau mot de passe
            </label>
            <input 
              type="password"
              style={{
                width: '100%', padding: '1rem 1.2rem',
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                borderRadius: '12px', color: 'white',
                fontSize: '1rem', outline: 'none'
              }}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Laisser vide pour ne pas changer"
              minLength="6"
            />
          </div>

          <button 
            type="submit" 
            style={{ 
              width: '100%', marginTop: '1rem', padding: '0.9rem',
              backgroundColor: 'var(--primary-color)',
              border: 'none', borderRadius: '10px',
              color: 'white', fontSize: '1rem', fontWeight: '600',
              cursor: 'pointer', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.5rem',
              transition: 'background-color 0.2s, opacity 0.2s',
              opacity: loading ? 0.7 : 1
            }}
            onMouseOver={(e) => { if(!loading) e.currentTarget.style.backgroundColor = 'var(--secondary-color)'; }}
            onMouseOut={(e) => { if(!loading) e.currentTarget.style.backgroundColor = 'var(--primary-color)'; }}
            disabled={loading}
          >
            <Save size={20} /> {loading ? 'Vérification...' : 'Sécuriser et Enregistrer'}
          </button>
        </form>

      </div>
    </div>
  );
};

export default AccountSettings;
